import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import MultiSelect from '@/components/ui/multi-select'
import { Loader2 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import {
  ClinicProgramOption,
  CreateOptionPayload,
  createClinicProgramOption,
  updateClinicProgramOption,
} from '@/services/clinicProgramOptionsService'

type ProgramOptionForForm = ClinicProgramOption & {
  label?: string
  description?: string | null
  intake_form_type?: string | null
  sort_order?: number | null
}

const BILLABLE_SERVICE_OPTIONS = [
  { value: 'MDI Visit', label: 'MDI Visit', description: 'Medical decision integration visit' },
  { value: 'Lab Order', label: 'Lab Order', description: 'Laboratory testing' },
  { value: 'At-Home Phlebotomy', label: 'At-Home Phlebotomy', description: 'Mobile blood draw service' },
  { value: 'Consulting Visit', label: 'Consulting Visit', description: 'Provider consultation' },
]

const formatCurrency = (cents: number) => `$${(cents / 100).toFixed(2)}`

const programOptionSchema = z
  .object({
    label: z.string().min(1, 'Label is required').max(255),
    description: z.string().max(2000).optional().or(z.literal('')),
    programPrice: z
      .string()
      .min(1, 'Program price is required')
      .refine(
        (val) => {
          const n = parseFloat(val)
          return !Number.isNaN(n) && n >= 1
        },
        { message: 'Program price must be at least $1.00' }
      ),
    facilitationFee: z
      .string()
      .min(1, 'Facilitation fee is required')
      .refine(
        (val) => {
          const n = parseFloat(val)
          return !Number.isNaN(n) && n >= 0
        },
        { message: 'Facilitation fee must be $0 or more' }
      ),
    intakeFormType: z
      .string()
      .min(1, 'Intake form is required')
      .refine((val) => val !== 'none', { message: 'Intake form is required' }),
    isSubscription: z.boolean(),
    frequency: z.string(),
    includesServices: z.array(z.string()),
  })
  .refine(
    (data) => {
      const price = parseFloat(data.programPrice || '0')
      const fee = parseFloat(data.facilitationFee || '0')
      return fee < price
    },
    { message: 'Clinic facilitation fee must be less than program price', path: ['facilitationFee'] }
  )
  .refine(
    (data) => {
      if (!data.isSubscription) return true
      return !!data.frequency
    },
    { message: 'Subscription frequency is required', path: ['frequency'] }
  )

type ProgramOptionFormValues = z.infer<typeof programOptionSchema>

const defaultValues: ProgramOptionFormValues = {
  label: '',
  description: '',
  programPrice: '',
  facilitationFee: '',
  intakeFormType: 'none',
  isSubscription: false,
  frequency: 'monthly',
  includesServices: [],
}

interface ProgramOptionFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  clinicId: string
  programId: string
  option: ProgramOptionForForm | null
  onSuccess: () => void
}

const ProgramOptionFormDialog: React.FC<ProgramOptionFormDialogProps> = ({
  open,
  onOpenChange,
  clinicId,
  programId,
  option,
  onSuccess,
}) => {
  const { toast } = useToast()
  const isEditing = !!option

  const form = useForm<ProgramOptionFormValues>({
    resolver: zodResolver(programOptionSchema),
    defaultValues,
  })

  const [saving, setSaving] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const programPrice = form.watch('programPrice')
  const facilitationFee = form.watch('facilitationFee')
  const isSubscription = form.watch('isSubscription')

  const platformFeeDisplay = (() => {
    const priceCents = Math.round(parseFloat(programPrice || '0') * 100)
    const feeCents = Math.round(parseFloat(facilitationFee || '0') * 100)
    if (priceCents > 0 && feeCents >= 0 && feeCents < priceCents) {
      return formatCurrency(priceCents - feeCents)
    }
    return '—'
  })()

  useEffect(() => {
    if (!open) return
    if (option) {
      const opt = option as ProgramOptionForForm
      form.reset({
        label: opt.label ?? '',
        description: opt.description ?? '',
        programPrice: (option.program_price / 100).toFixed(2),
        facilitationFee: (option.clinic_facilitation_fee / 100).toFixed(2),
        intakeFormType: opt.intake_form_type ?? 'none',
        isSubscription: option.is_subscription,
        frequency: option.subscription_frequency || 'monthly',
        includesServices: Array.isArray(option.includes_services) ? option.includes_services : [],
      })
    } else {
      form.reset(defaultValues)
    }
    setSubmitError('')
  }, [open, option, form])

  const onSubmit = async (values: ProgramOptionFormValues) => {
    const priceCents = Math.round(parseFloat(values.programPrice || '0') * 100)
    const feeCents = Math.round(parseFloat(values.facilitationFee || '0') * 100)

    const payload: CreateOptionPayload = {
      label: values.label.trim(),
      description: values.description?.trim() || undefined,
      program_price: priceCents,
      clinic_facilitation_fee: feeCents,
      includes_services: values.includesServices.length > 0 ? values.includesServices : undefined,
      is_subscription: values.isSubscription,
      subscription_frequency: values.isSubscription
        ? (values.frequency as 'weekly' | 'monthly' | 'quarterly')
        : undefined,
      intake_form_type: values.intakeFormType as
        | 'trt'
        | 'hrt'
        | 'glp1'
        | 'peptide'
        | 'nad'
        | 'fertility'
        | 'general',
    }

    try {
      setSaving(true)
      setSubmitError('')
      if (isEditing && option) {
        await updateClinicProgramOption(clinicId, programId, option.id, payload)
        toast({ title: 'Updated', description: `"${values.label}" has been updated` })
      } else {
        await createClinicProgramOption(clinicId, programId, payload)
        toast({ title: 'Created', description: `"${values.label}" has been added` })
      }
      onSuccess()
      onOpenChange(false)
    } catch (err: unknown) {
      const msg =
        (err as { message?: string })?.message || 'Failed to save option'
      setSubmitError(msg)
      toast({ title: 'Error', description: msg, variant: 'destructive' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-lg max-h-[90vh] flex flex-col overflow-hidden'>
        <DialogHeader className='flex-shrink-0'>
          <DialogTitle>{isEditing ? 'Edit' : 'Add'} Program Option</DialogTitle>
        </DialogHeader>

        <div className='flex-1 min-h-0 overflow-y-auto -mx-1 px-1'>
          <Form {...form}>
            <form id='program-option-form' onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
              <FormField
                control={form.control}
                name='label'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='required'>Label</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder='e.g. GLP-1 Protocol' maxLength={255} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='description'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder='Optional description...'
                        rows={2}
                        maxLength={2000}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='intakeFormType'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='required'>Intake Form</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder='Select intake form' />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value='none'>Select an intake form</SelectItem>
                        <SelectItem value='trt'>Men's Health (TRT)</SelectItem>
                        <SelectItem value='hrt'>Women's Health (HRT)</SelectItem>
                        <SelectItem value='glp1'>Weight Loss (GLP-1)</SelectItem>
                        <SelectItem value='peptide'>Peptide Healing</SelectItem>
                        <SelectItem value='nad'>NAD+</SelectItem>
                        <SelectItem value='fertility'>Fertility</SelectItem>
                        <SelectItem value='general'>General</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className='text-xs text-muted-foreground'>
                      Intake form patients will complete when purchasing this option
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className='grid grid-cols-2 gap-3'>
                <FormField
                  control={form.control}
                  name='programPrice'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className='required'>Total Fee</FormLabel>
                      <div className='relative'>
                        <span className='absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500'>$</span>
                        <FormControl>
                          <Input
                            {...field}
                            type='number'
                            step='0.01'
                            min={1}
                            className='pl-7'
                            placeholder='349.00'
                          />
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='facilitationFee'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className='required'>Clinic Facilitation Fee</FormLabel>
                      <div className='relative'>
                        <span className='absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500'>$</span>
                        <FormControl>
                          <Input
                            {...field}
                            type='number'
                            step='0.01'
                            min={0}
                            className='pl-7'
                            placeholder='50.00'
                          />
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className='space-y-1.5'>
                <FormLabel className='text-muted-foreground'>Platform Fee</FormLabel>
                <div className='rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700'>
                  {platformFeeDisplay}
                </div>
                <p className='text-xs text-muted-foreground'>
                  Total Fee minus Clinic Facilitation Fee
                </p>
              </div>

              <FormField
                control={form.control}
                name='includesServices'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Included Services</FormLabel>
                    <MultiSelect
                      options={BILLABLE_SERVICE_OPTIONS}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder='Select included services...'
                    />
                    <p className='text-xs text-muted-foreground'>
                      Selected services will not be charged separately to the clinic
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='isSubscription'
                render={({ field }) => (
                  <FormItem>
                    <div className='flex items-center justify-between rounded-lg border border-slate-200 p-4'>
                      <div>
                        <FormLabel>Is Subscription</FormLabel>
                        <p className='text-xs text-muted-foreground mt-0.5'>
                          One-time payment when off, recurring when on
                        </p>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {isSubscription && (
                <FormField
                  control={form.control}
                  name='frequency'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className='required'>Subscription Frequency</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder='Select frequency' />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value='weekly'>Weekly</SelectItem>
                          <SelectItem value='monthly'>Monthly</SelectItem>
                          <SelectItem value='quarterly'>Quarterly</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {submitError && <p className='text-sm text-destructive'>{submitError}</p>}
            </form>
          </Form>
        </div>

        <DialogFooter className='flex-shrink-0'>
          <Button
            type='button'
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button type='submit' form='program-option-form' disabled={saving}>
            {saving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            {isEditing ? 'Save Changes' : 'Add Option'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ProgramOptionFormDialog
